/**
 * GalleryAlbumView — album detail view with photo grid, lightbox and download.
 * Route: /:community/galeria/:albumId
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COPY = {
  ca: { back: '← Galeria', downloadAll: 'Descarregar tot (ZIP)', downloadOne: 'Descarregar', photos: 'fotos', notFound: 'Àlbum no trobat.' },
  es: { back: '← Galería', downloadAll: 'Descargar todo (ZIP)', downloadOne: 'Descargar', photos: 'fotos', notFound: 'Álbum no encontrado.' },
  en: { back: '← Gallery', downloadAll: 'Download all (ZIP)', downloadOne: 'Download', photos: 'photos', notFound: 'Album not found.' },
  fr: { back: '← Galerie', downloadAll: 'Tout télécharger (ZIP)', downloadOne: 'Télécharger', photos: 'photos', notFound: 'Album introuvable.' },
};

const withApi = (u) => (u && u.startsWith('/api/') ? `${API_URL}${u}` : u);

export const GalleryAlbumView = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { community, albumId } = useParams();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const copy = COPY[(i18n.language || 'ca').split('-')[0]] || COPY.ca;

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/gallery/albums/${albumId}`)
      .then((r) => setAlbum(r.data))
      .catch(() => setAlbum(null))
      .finally(() => setLoading(false));
  }, [albumId]);

  const titleFor = () => album?.title?.[i18n.language.split('-')[0]] || album?.title?.ca || 'Àlbum';
  const descFor = () => album?.description?.[i18n.language.split('-')[0]] || album?.description?.ca || '';

  const downloadOne = (p) => {
    const a = document.createElement('a');
    a.href = withApi(p.url);
    a.download = p.filename || 'photo.jpg';
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadZip = () => {
    window.open(`${API_URL}/api/gallery/albums/${albumId}/download`, '_blank');
  };

  const photos = album?.photos || [];
  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prev = () => setLightboxIdx((i) => (i === 0 ? photos.length - 1 : i - 1));
  const next = () => setLightboxIdx((i) => (i === photos.length - 1 ? 0 : i + 1));

  return (
    <div className="min-h-screen bg-[#0E4D2A] text-[#F2E8D5] pb-24" data-testid="gallery-album-view">
      <div className="max-w-6xl mx-auto px-5 md:px-10 pt-8 md:pt-12">
        <button
          type="button"
          onClick={() => navigate(`/${community}/galeria`)}
          className="text-[#A8B4A6] hover:text-[#D4AF37] text-sm tracking-[0.2em] font-sans transition-colors"
          data-testid="album-back-btn"
        >
          {copy.back}
        </button>

        {loading ? (
          <div className="text-center text-[#A8B4A6] py-20">…</div>
        ) : !album ? (
          <div className="text-center text-[#A8B4A6] py-20" data-testid="album-not-found">{copy.notFound}</div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mt-6 mb-10"
            >
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl" data-testid="album-title">{titleFor()}</h1>
              {descFor() && (
                <p className="mt-3 text-[#A8B4A6] italic text-sm md:text-base max-w-2xl mx-auto">{descFor()}</p>
              )}
              <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-[#A8B4A6]/70 font-sans">
                {photos.length} {copy.photos}
              </div>
              {photos.length > 0 && (
                <button
                  type="button"
                  onClick={downloadZip}
                  className="mt-5 inline-flex items-center gap-2 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#D4AF37] px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase transition-colors font-sans"
                  data-testid="album-download-zip"
                >
                  ⬇ {copy.downloadAll}
                </button>
              )}
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4" data-testid="album-photo-grid">
              {photos.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.02 }}
                  whileHover={{ scale: 1.02 }}
                  className="relative aspect-square bg-[#083B1F] rounded overflow-hidden cursor-pointer group"
                  onClick={() => openLightbox(i)}
                  data-testid={`album-photo-${p.id}`}
                >
                  <img
                    src={withApi(p.url)}
                    alt={p.filename}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                  />
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && photos[lightboxIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
            data-testid="album-lightbox"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl flex items-center justify-center"
              aria-label="Previous"
            >
              ‹
            </button>
            <img
              src={withApi(photos[lightboxIdx].url)}
              alt={photos[lightboxIdx].filename}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white text-2xl flex items-center justify-center"
              aria-label="Next"
            >
              ›
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); downloadOne(photos[lightboxIdx]); }}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/10 hover:bg-white/25 text-white text-xs tracking-[0.2em] uppercase font-sans px-5 py-2 rounded-full"
            >
              ⬇ {copy.downloadOne}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryAlbumView;
